// [S-092] Attestation detail — single-template evidence chain.
//
// Direct port of docs/Wireframes/Attestation.html. The 5-step chain (CBA
// anchor → draft → peer review → attorney certification → re-attestation
// schedule) renders from the entity itself: anchorCBA, submittedBy/
// effectiveFrom, lifecycleState, and reAttestationDueAt are all already on
// `LifecycleTemplate`. Peer review + counsel draft step bodies are
// placeholder copy until the audit log surfaces those events — explicitly
// noted on the row, not silently faked.
//
// "Verify chain" recomputes sha256(content) in the browser and compares
// to the server-returned preImageHash. The whole pre-image is shipped in
// the detail payload (server keeps `content` in the entity), so the check
// is genuine — pass means the bytes the server returned hash to the
// digest it claims.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, PageHead, PageLoading } from "../components";
import {
  recomputeContentHash,
  useAttestationDetail,
} from "../data/attestationsQuery";
import { useActorLabels } from "../data/identitiesQuery";

type VerifyState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "ok"; recomputed: string }
  | { kind: "fail"; recomputed: string };

export function AttestationDetail(): JSX.Element {
  const { templateId } = useParams<{ templateId: string }>();
  const id = templateId ?? "";
  const { data, isLoading, isError, error } = useAttestationDetail(id);
  const [verify, setVerify] = useState<VerifyState>({ kind: "idle" });
  // Called unconditionally (before the loading/error early returns) per the
  // rules of hooks — degrades to an empty uid list pre-load.
  const actorUids = [
    ...(data !== undefined ? [data.attestedBy] : []),
    ...(data?.peerReviewerUid != null && data.peerReviewerUid.length > 0
      ? [data.peerReviewerUid]
      : []),
  ];
  const actorLabels = useActorLabels(actorUids, data?.tenantId ?? "");

  if (isLoading || data === undefined) {
    if (isError && (error as { status?: number } | undefined)?.status === 404) {
      return <NotFound />;
    }
    return (
      <PageLoading
        eyebrow="Approval record"
        sub="Fetching the approval trail."
        label="Loading attestation…"
      />
    );
  }

  if (isError) {
    if ((error as { status?: number } | undefined)?.status === 404) {
      return <NotFound />;
    }
    return (
      <PageHead
        eyebrow="Approval record"
        title="Record unavailable"
        sub="The admin API responded with an error. Try again in a moment."
      />
    );
  }

  const attestedByLabel = actorLabels?.get(data.attestedBy) ?? "—";
  const peerReviewerLabel =
    data.peerReviewerUid != null && data.peerReviewerUid.length > 0
      ? (actorLabels?.get(data.peerReviewerUid) ?? "—")
      : null;

  const onVerify = (): void => {
    setVerify({ kind: "running" });
    void recomputeContentHash(data.content).then((digest) => {
      setVerify({
        kind: digest === data.preImageHash ? "ok" : "fail",
        recomputed: digest,
      });
    });
  };

  return (
    <>
      <PageHead
        eyebrow={
          <>
            <Link to="/attestation">Approvals</Link>{" "}
            <span style={{ color: "var(--cs-text-secondary)" }}>
              · {titleFor(data)}
            </span>
          </>
        }
        title={
          <>
            The chain of <em>custody</em>.
          </>
        }
        sub="Every certified template carries a verifiable approval trail — who reviewed it, what CBA it's anchored to, and a tamper-evident fingerprint of the approved document."
        cta={
          <>
            <VerifyBadge state={verify} />
            <Button
              variant="primary"
              className="btn--s"
              onClick={onVerify}
              disabled={verify.kind === "running"}
              data-testid="att-detail-verify"
            >
              {verify.kind === "running" ? "Verifying…" : "Verify integrity ✓"}
            </Button>
          </>
        }
      />

      <div className="att__seal" data-testid="att-detail-seal">
        <div className="att__seal__circle" aria-hidden="true">
          ✓
        </div>
        <div>
          <div className="att__seal__title">{titleFor(data)}</div>
          <div className="att__seal__sub">
            {data.layerSource === "L1" ? "L1 (platform)" : "L2 (studio)"} ·
            attested {formatLongDate(data.attestedAt)} by {attestedByLabel}
            {data.reAttestationDueAt !== null
              ? ` · valid through ${formatLongDate(data.reAttestationDueAt)}`
              : ""}
          </div>
        </div>
        <div className="att__seal__hash">
          <b>Document fingerprint</b>
          <span data-testid="att-detail-hash">
            {data.preImageHash.slice(0, 12)}
            <br />
            {data.preImageHash.slice(12, 24)}…{data.preImageHash.slice(-4)}
          </span>
        </div>
      </div>

      <div className="att__chain" data-testid="att-detail-chain">
        <div className="att__chain__lbl">Approval trail · 5 steps</div>

        <ChainStep
          n={1}
          title="Anchored to CBA"
          meta={
            data.anchorCBA === null
              ? "No anchor CBA recorded for this template."
              : `${data.anchorCBA} — the collective bargaining agreement this template is anchored to.`
          }
          hash={
            data.anchorCBA === null ? null : `cba: ${data.anchorCBA.toLowerCase()}`
          }
          when={null}
        />

        <ChainStep
          n={2}
          // [S-092 follow-up] In the current platform model the
          // drafting attorney is the same person as the certifying
          // attorney (data.attestedBy / submittedBy). When the
          // platform later separates "drafter" from "certifier" as
          // distinct roles, this step gets its own field.
          title="Drafted by counsel of record"
          meta={`${attestedByLabel} drafted this template against the anchor CBA and industry precedent.`}
          hash={null}
          when={null}
        />

        <ChainStep
          n={3}
          title="Peer review"
          // [S-092 follow-up / S-221] When peerReviewerUid is set the
          // step rendered with the actual handoff state — assigned,
          // pending, or recorded. Counsel-draft step (n=2) stays
          // placeholder because there's no draft-metadata source yet.
          meta={
            peerReviewerLabel === null
              ? "No peer reviewer assigned to this template. Peer review is a 4-eyes check separate from certification."
              : data.peerReviewedAt == null
                ? `Assigned to ${peerReviewerLabel} — pending. The reviewer marks completion via the Record review action.`
                : `Reviewed by ${peerReviewerLabel}.`
          }
          hash={peerReviewerLabel === null ? null : `peer: ${peerReviewerLabel}`}
          when={
            data.peerReviewedAt == null
              ? null
              : formatShortMonthYear(data.peerReviewedAt)
          }
        />

        <ChainStep
          n={4}
          title={
            data.lifecycleState === "re_certified"
              ? "Re-certified by attorney of record"
              : "Certified by attorney of record"
          }
          meta={`${attestedByLabel} attested the template as ${prettyDealType(data.dealType)} (${data.guild}, ${data.tier}).`}
          hash={`attest: ${data.preImageHash.slice(0, 12)}…${data.preImageHash.slice(-4)}`}
          when={formatShortMonthYear(data.attestedAt)}
        />

        <ChainStep
          n={5}
          title="Re-attestation scheduled"
          meta={
            data.reAttestationDueAt === null
              ? "No re-attestation date set for this template."
              : `Re-attestation against current CBA — alert 14 days before due date.`
          }
          hash={
            data.reAttestationDueAt === null
              ? null
              : `due: ${data.reAttestationDueAt.slice(0, 10)}`
          }
          when={
            data.reAttestationDueAt === null
              ? null
              : formatShortMonthYear(data.reAttestationDueAt)
          }
        />
      </div>

      {verify.kind === "fail" && (
        <div
          role="alert"
          style={{ color: "var(--cs-risk-high-text)" }}
          data-testid="att-detail-verify-fail"
        >
          Hash mismatch. The server-returned digest does not match the
          recomputation over the document content. Recomputed:{" "}
          <code>{verify.recomputed}</code>
        </div>
      )}
    </>
  );
}

function VerifyBadge({ state }: { state: VerifyState }): JSX.Element | null {
  if (state.kind === "idle") return null;
  if (state.kind === "running") {
    return (
      <span className="att__verify att__verify--pending">recomputing…</span>
    );
  }
  if (state.kind === "ok") {
    return (
      <span className="att__verify att__verify--ok" data-testid="att-detail-verify-ok">
        ✓ chain verified
      </span>
    );
  }
  return (
    <span className="att__verify att__verify--fail">⚠ hash mismatch</span>
  );
}

function ChainStep({
  n,
  title,
  meta,
  hash,
  when,
}: {
  n: number;
  title: string;
  meta: string;
  hash: string | null;
  when: string | null;
}): JSX.Element {
  return (
    <div className="att__step" data-testid={`att-detail-step-${n}`}>
      <span className="att__step__num">{n}</span>
      <div>
        <div className="att__step__title">{title}</div>
        <div className="att__step__meta">{meta}</div>
        {hash !== null && <div className="att__step__hash">{hash}</div>}
      </div>
      <div className="att__step__when">{when ?? ""}</div>
    </div>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="empty" role="status">
      <div className="empty__eyebrow">Not found</div>
      <h1 className="empty__title">No attestation matches this id.</h1>
      <p className="empty__lede">
        The template may have been deprecated, or it lives in an organization you
        don't have access to.{" "}
        <Link to="/attestation">Return to attestation records</Link>.
      </p>
    </div>
  );
}

function titleFor(d: { dealType: string; guild: string }): string {
  return `${prettyGuild(d.guild)} ${prettyDealType(d.dealType)}`;
}

function prettyDealType(s: string): string {
  return s
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function prettyGuild(g: string): string {
  return g.toUpperCase();
}

function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatShortMonthYear(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}
